import LFCDataGrid from '_components/datagrid/LFCDataGrid';
import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import React, {useState} from 'react';
import Draggable from 'react-draggable';

import {Box, Dialog, DialogContent, useTheme, Theme} from '@mui/material';
import LFCIconButton from '_components/inputs/LFCIconButton';
import Paper, {PaperProps} from '@mui/material/Paper';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {saveAsCrossOriginCaptureImage} from '_components/common/ScreenCapture';
import {makeStyles} from 'tss-react/mui';

// スタイル定義
const useStyles = makeStyles()((theme: Theme) => ({
  captureButton: {
    position: 'absolute',
    right: theme.spacing(5.5), // 約45pxに相当
    top: theme.spacing(1)
  }
}));

/**
 * 引数
 */
interface Props {
  open: boolean;
  onClose: () => void;
  showDetailPath: string;
  dsRow?: any;
  dsColumns?: any;
}

function PaperComponent(props: PaperProps) {
  // Refを使ってDOMノードを直接参照し、findDOMNode警告を回避
  const nodeRef = React.useRef<HTMLDivElement>(null);

  return (
    <Draggable nodeRef={nodeRef}>
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
}

/**
 * 画像表示ダイアログ
 * @param props
 * @returns
 */
const ImageDialog = (props: Props) => {
  const {classes} = useStyles(); // スタイルフックを使用

  // 画像の位置情報を管理するための状態変数
  const [position, setPosition] = useState({x: 0, y: 0});

  // テーマから背景色を取得
  const theme = useTheme();
  const backgroundColor = theme.palette.background.paper;

  // ドラッグ開始時の処理
  const handleDragStart = (e: React.DragEvent<HTMLImageElement>) => {
    e.preventDefault();
  };

  // ドラッグ中の処理
  const handleDrag = (e: React.DragEvent<HTMLImageElement>) => {
    e.preventDefault();
    setPosition({
      x: position.x + e.movementX,
      y: position.y + e.movementY
    });
  };

  const [isImageLoaded, setIsImageLoaded] = useState(true);

  const handleImageError = () => {
    setIsImageLoaded(false);
  };

  // ユニークなIDを生成
  const dialogId = 'inspection-image-dialog';

  // キャプチャ保存ボタンのクリックハンドラ
  const handleCaptureClick = async () => {
    // キャプチャの実行（テーマの背景色を指定）
    await saveAsCrossOriginCaptureImage(dialogId, 'Inspection', backgroundColor);
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth={false}
      fullWidth
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      disableEnforceFocus // フォーカス強制を無効化してaria-hidden警告を防ぐ
      disableRestoreFocus // フォーカス復元を無効化
    >
      <div id={dialogId}>
        {isImageLoaded && (
          <LFCDialogTitle onClose={props.onClose}>
            画像
            <LFCIconButton onClick={() => handleCaptureClick()} className={classes.captureButton}>
              <PhotoCameraIcon />
            </LFCIconButton>
          </LFCDialogTitle>
        )}
        <DialogContent
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            height: '70vh'
          }}
        >
          <Box
            style={{
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%'
            }}
          >
            <img
              src={props.showDetailPath}
              alt=""
              style={{
                position: 'relative',
                left: position.x,
                top: position.y,
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
              draggable
              onDragStart={handleDragStart}
              onDrag={handleDrag}
              onError={handleImageError}
            />
          </Box>
        </DialogContent>
        {props.dsColumns && props.dsRow && (
          <LFCDataGrid
            columns={props.dsColumns.filter((col: any) => col.headerName !== 'rn')}
            rows={props.dsRow}
            columnOrder={[
              {name: 'job_type_name', order: 0},
              {name: 'map', order: 1},
              {name: 'branch_no', order: 2}
            ]}
            style={{cursor: 'pointer'}}
            initialState={{
              left: ['job_type_name', 'map', 'branch_no', 'judge', 'img_filename'],
              right: ['actions']
            }}
            getRowClassName={(params: any) => `grid-css--${params.row.judge}`}
            hideFooter
            height="110px"
            autoHeight={true}
            pagination={false}
          />
        )}
      </div>
    </Dialog>
  );
};

export default ImageDialog;
